import { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { Button, useNotify, Alert, tokens, Modal } from '@hotjar/hotsauce';
import styled from '@emotion/styled';

import { useTranslationWithPrefix } from '@hotjar/translations';
import { useCurrentUser } from '@hotjar/user';
import { useOrganizationsInvalidate } from '@hotjar/webapp/features/Settings/SitesAndOrganizations/useOrganizationsInvalidate';

import { Event, UiPosition } from '../../events';
import { useModalEvents } from '../../useModalEvents';
import type { OrganizationModel } from '../model';
import { TransferOrganizationForm } from './TransferOrganizationForm';
import * as api from './api';

interface Props {
  handleSuccess(): void;
  handleCancel(): void;
  opened: boolean;
  organization: OrganizationModel;
}

const TRANSLATION_DOMAIN = 'account:transfer-organization';
const FORM_ID = 'transferOrganizationForm';

const S = {
  Alert: styled(Alert)`
    margin-bottom: ${tokens.spacer.x3};
  `,
};

export function TransferOrganizationModal({
  opened,
  handleCancel,
  handleSuccess,
  organization,
}: Props): React.ReactElement {
  const { data: currentUser } = useCurrentUser();
  const [serverError, setServerError] = useState('');
  const { t: tAccount } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const { t: tCommon } = useTranslationWithPrefix('common:actions');
  const { showToast } = useNotify();
  const invalidate = useOrganizationsInvalidate();
  const { trackClicked, trackOpened } = useModalEvents({
    organizationId: organization.id,
    accountId: organization.accountId,
    confirm: Event.CONFIRM_TRANSFER_OWNERSHIP_MODAL,
    uiPosition: UiPosition.TRANSFER_OWNERSHIP_MODAL,
    opened: Event.VIEW_TRANSFER_OWNERSHIP_MODAL,
  });

  const mutation = useMutation(
    ({ password, newOwner }: { password: string; newOwner: string }) => {
      return api.transferOrganization({
        accountId: organization.accountId,
        organizationId: organization.id,
        password,
        userId: window.parseInt(newOwner, 10),
      });
    },
    {
      onSuccess: () => {
        showToast({
          text: tAccount('transfer-success'),
          kind: 'info',
        });
        invalidate();
        handleSuccess();
      },
      onError: (error: Error) => {
        setServerError(error.message);
        showToast({ text: tAccount('transfer-failure'), kind: 'danger' });
      },
    },
  );

  useEffect(() => {
    trackOpened(opened);
  }, [opened, trackOpened]);

  const users = organization.users.filter(({ id }) => id !== currentUser?.id);

  return (
    <Modal isOpen={opened} size="m" contentLabel={tAccount('title')} onClose={() => handleCancel()}>
      <Modal.Header title={tAccount('title')} />
      <Modal.Body>
        {users.length === 0 && <S.Alert kind="info">{tAccount('no-users-info')}</S.Alert>}
        <TransferOrganizationForm
          onSubmit={(data) => {
            setServerError('');
            trackClicked();
            mutation.mutate(data);
          }}
          formId={FORM_ID}
          users={users}
        />
        {serverError && <Alert kind="danger">{serverError}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={() => handleCancel()} isDisabled={mutation.isLoading}>
          {tCommon('cancel')}
        </Button>
        <Button kind="primary" type="submit" form={FORM_ID} isLoading={mutation.isLoading}>
          {tAccount('transfer')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
